import type { Student, Group, Settings } from "../../types/models";
import { findLeastFullGroup } from "./utils";
import { chooseColor } from "../../utils/colors";

/**
 * Check if a student is already placed in any group
 */
function isPlaced(student: Student, groups: Group[]): boolean {
    return groups.some(g => g.memberIds.includes(student.id));
}


/**
 * Process tags with 'group' mode - keep students with same tag together
 * This runs AFTER must-together placement but BEFORE greedy placement
 */
export function processTagGrouping(
    students: Student[],
    groups: Group[],
    settings: Settings
): void {
    // Find all tags configured to 'group' mode
    const groupingTags = Array.from(settings.tagBehavior.entries())
        .filter(([_, mode]) => mode === 'group')
        .map(([tag, _]) => tag);

    for (const tag of groupingTags) {
        // Get all students with this tag who aren't placed yet
        const taggedStudents = students.filter(s =>
            s.tags.includes(tag) && !isPlaced(s, groups)
        );

        if (taggedStudents.length === 0) continue;

        // Distribute these students together (split if > maxSize)
        distributeTagGroup(taggedStudents, groups, settings);
    }
}

/**
 * Distribute students with same tag into groups
 * Tries to keep them together, splits across multiple groups if needed
 */
function distributeTagGroup(
    students: Student[],
    groups: Group[],
    settings: Settings
): void {
    // Track which groups we're using for this tag
    const tagGroups: Group[] = [];

    for (const student of students) {
        // Try to add to an existing tag group that has capacity
        const targetGroup = tagGroups.find(g =>
            g.memberIds.length < settings.maxSize
        );

        if (targetGroup) {
            // Add to existing tag group
            targetGroup.memberIds.push(student.id);
        } else {
            // Need a new group for this tag
            // Try to find an empty group first
            const emptyGroup = groups.find(g => g.memberIds.length === 0);

            if (emptyGroup) {
                // Use empty group
                emptyGroup.memberIds.push(student.id);
                tagGroups.push(emptyGroup);
            } else if (!settings.maxGroups || groups.length < settings.maxGroups) {
                // Create new group (only if maxGroups allows)
                const newGroup: Group = {
                    id: `g${groups.length + 1}`,
                    memberIds: [student.id],
                    color: chooseColor(groups.length)
                };
                groups.push(newGroup);
                tagGroups.push(newGroup);
            } else {
                // Max groups reached - force into least full group
                const leastFull = findLeastFullGroup(groups);
                if (leastFull) {
                    leastFull.memberIds.push(student.id);
                }
            }
        }
    }
}

